import React from 'react';
import { cn } from '~/lib/utils';
import styles from './ActivityList.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ActivityListProps {
  /** `ActivityListRow`s. Proven at two (Notion), three (most), and four (both Outline boards). */
  children: React.ReactNode;
  /**
   * The accessible name for the card, e.g. `Outline steps`.
   *
   * Optional because the card is almost always sitting directly under an `ActivityRow` that
   * already names it ("Building outline", "2 of 6") — naming it twice would make a screen reader
   * read the heading, then read it again as the list's label.
   */
  label?: string;
  /**
   * Whether the card carries its own `aria-live="polite"` region. Default `true`.
   *
   * Same invariant as `ActivityRow`'s prop of the same name: **exactly one live region per
   * announcing subtree, and a standalone card still announces.** A card rendered on its own — this
   * file's stories, `ActivityListRow`'s stories, any caller holding a bare list of steps — has no
   * ancestor region, so it keeps this one and its rows' state words ride inside it.
   *
   * `ActivityBlock` passes `false`, because there the card is one of six bodies that can appear,
   * disappear or replace each other under a header, and only the block can see that happen. A card
   * whose region is switched off is not silent: it sits inside the block's region, so a step going
   * Not started -> Running is still announced, just by the element one level up.
   */
  announce?: boolean;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The bordered card a run's steps live in (*Running*, all ten list-bodied `Agent tool` boards,
 * *Interrupted*, the *Engineer · research to document* thread).
 *
 * Six agents proposed it — `ToolRunSkeletonCard`, `AgentCard`, `OutlineList`, `AgentArtifactList`,
 * `ToolListBody`, `StepList` — and every one of them drew the same box. It has **no background**,
 * which is the thing to hold on to: a filled card would read as a surface floating over the
 * thread, and this is meant to read as a region *of* the thread that happens to be ruled off.
 *
 * Its one near-twin is `SkeletonCard`, which shares the radius and the rim and differs in three
 * numbers (14px padding all round, a 9px gap, and no `overflow: clip`). They are separate
 * components because the clip is not cosmetic here — see the note on it in the stylesheet.
 *
 * Not props: the 4px block inset, the absence of a gap, the absence of dividers, and the clip.
 */
export function ActivityList({
  children,
  label,
  announce = true,
  theme: themeProp,
  className,
}: ActivityListProps) {
  const theme = useV15Theme(themeProp);
  return (
    <ul
      className={cn(chatThemeClass(theme), styles.root, className)}
      aria-label={label}
      /* The rows change under the user while they are reading the prose below — a step going from
         Not started to Running to Done is exactly the "status that changes while you are not
         looking" case. The region sits on the card rather than on each row so that a run which
         advances two steps at once is announced as one update instead of two — and it comes off
         the card entirely when a block is already speaking for it (`announce={false}`), so the
         subtree never has two owners for one announcement. */
      aria-live={announce ? 'polite' : undefined}
    >
      {children}
    </ul>
  );
}
